import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Users, MapPin, Check, X, Minus } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatTimeRangeUhr, formatScheduleLabel } from '@/lib/utils';

export default function LessonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingAttendance, setUpdatingAttendance] = useState(false);

  useEffect(() => {
    loadLesson();
  }, [id]);

  const loadLesson = async () => {
    try {
      const response = await api.get(`/lessons/${id}`);
      setLesson(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Termins');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  };

  const initializeAttendance = async () => {
    try {
      await api.post(`/lessons/${id}/init-attendance`);
      toast.success('Anwesenheit initialisiert');
      loadLesson();
    } catch (error) {
      toast.error('Fehler beim Initialisieren der Anwesenheit');
    }
  };

  const updateAttendanceStatus = async (studentId, status) => {
    setUpdatingAttendance(true);
    try {
      await api.post(`/lessons/${id}/attendance`, {
        student_id: studentId,
        status: status
      });
      toast.success('Anwesenheit aktualisiert');
      loadLesson();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Anwesenheit');
    } finally {
      setUpdatingAttendance(false);
    }
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present':
        return <Check className="w-5 h-5 text-emerald-600" />;
      case 'absent':
        return <X className="w-5 h-5 text-rose-600" />;
      case 'excused':
        return <Minus className="w-5 h-5 text-brand-warning" />;
      default:
        return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getAttendanceBadge = (status) => {
    const variants = {
      present: { label: 'Anwesend', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      absent: { label: 'Abwesend', className: 'bg-rose-50 text-rose-700 border-rose-200' },
      excused: { label: 'Entschuldigt', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const config = variants[status] || variants.present;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-4" data-testid="lesson-title">
            {formatScheduleLabel(lesson?.course_name) || lesson?.course_name}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Zeitpunkt</p>
                <p className="text-sm text-slate-900">
                  {formatDate(lesson?.date)}
                </p>
                <p className="text-sm text-slate-600">
                  {formatTimeRangeUhr(lesson?.start_time, lesson?.end_time)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Lehrkraft</p>
                <p className="text-sm text-slate-900">{lesson?.teacher_name || '-'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-500">Raum</p>
                <p className="text-sm text-slate-900">{lesson?.room_name || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Anwesenheit</h2>
            {lesson?.attendances?.length === 0 && (
              <Button
                onClick={initializeAttendance}
                size="sm"
                data-testid="init-attendance-button"
              >
                Anwesenheit initialisieren
              </Button>
            )}
          </div>

          {lesson?.attendances && lesson.attendances.length > 0 ? (
            <div className="space-y-3">
              {lesson.attendances.map((attendance) => (
                <div
                  key={attendance.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`attendance-${attendance.student_id}`}
                >
                  <div className="flex items-center gap-3">
                    {getAttendanceIcon(attendance.status)}
                    <div>
                      <p className="font-medium text-slate-900">{attendance.student_name}</p>
                      {attendance.notes && (
                        <p className="text-sm text-slate-600">{attendance.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getAttendanceBadge(attendance.status)}
                    <div className="flex gap-1 ml-3">
                      <Button
                        size="sm"
                        variant={attendance.status === 'present' ? 'default' : 'outline'}
                        onClick={() => updateAttendanceStatus(attendance.student_id, 'present')}
                        disabled={updatingAttendance}
                        data-testid={`mark-present-${attendance.student_id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={attendance.status === 'excused' ? 'default' : 'outline'}
                        onClick={() => updateAttendanceStatus(attendance.student_id, 'excused')}
                        disabled={updatingAttendance}
                        data-testid={`mark-excused-${attendance.student_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={attendance.status === 'absent' ? 'default' : 'outline'}
                        onClick={() => updateAttendanceStatus(attendance.student_id, 'absent')}
                        disabled={updatingAttendance}
                        data-testid={`mark-absent-${attendance.student_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Keine Anwesenheitsdaten vorhanden. Initialisieren Sie die Anwesenheit für diesen Termin.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}